// src/pages/manager/ManagerStudentList.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import { getUserListExportColumns } from '../../utils/userListExport';
import API from '../../api';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import ListRecordIdBadge from '../../components/common/ListRecordIdBadge';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { userListSortColumns } from '../../config/tableSortColumns';
import { resolveRowId } from '../../utils/tableSort';
import UserLink from '../../components/common/UserLink';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import UserRowActions from '../../components/common/UserRowActions';
import ImportStudentsButton from '../../components/common/ImportStudentsButton';
import { formatFullName } from '../../utils/name';
import { showConfirm } from '../../utils/popup';
import PlayingPeekCell from '../playing/PlayingPeekCell';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import { apiErrorMessage, safeUserMessage } from '../../utils/apiError';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const ManagerStudentList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('active');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    const loadStudents = useCallback(() => {
        setLoading(true);
        setLoadError(false);
        API.get('/manager/users', { params: { role: 'STUDENT' } })
            .then(res => setStudents(Array.isArray(res.data) ? res.data : []))
            .catch(err => {
                console.error('Error fetching students:', err);
                setStudents([]);
                setLoadError(true);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadStudents();
    }, [loadStudents, location.state?.fromForm]);

    const filtered = useMemo(() => {
        let result = students || [];
        if (filter === 'active') {
            result = result.filter(s => s.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(s => s.active === false);
        }
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => (x ?? '').toString().toLowerCase();
            result = result.filter(u =>
                [
                    u.firstName, u.middleName, u.lastName,
                    u.username || u.userName,
                    u.email,
                    u.phoneNumber || u.phone,
                    u.id || u.userId || u.user_id
                ].some(v => pick(v).includes(term))
            );
        }
        return result;
    }, [students, dq, filter]);

    const pagination = useSortedClientPagination(filtered, {
        columns: userListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const total = filter === 'all'
        ? students.length
        : filter === 'active'
            ? students.filter(s => s.active !== false).length
            : students.filter(s => s.active === false).length;

    const tabs = [
        { key: 'all', label: t('common.all'), count: students.length },
        { key: 'active', label: t('common.active'), count: students.filter(s => s.active !== false).length },
        { key: 'archived', label: t('common.archived'), count: students.filter(s => s.active === false).length },
    ];

    const removeFromMyList = async (student) => {
        const studentId = student.id ?? student.userId;
        const name = formatFullName(student.firstName, student.middleName, student.lastName) || student.username || t('pages.managerStudents.thisStudent');
        if (!await showConfirm(
            t('pages.managerStudents.removeConfirm', { name })
        )) return;

        try {
            const res = await API.delete(`/manager/students/${studentId}/remove-from-list`);
            alert(safeUserMessage(res.data?.message, t('common.done')));
            loadStudents();
        } catch (err) {
            console.error(err);
            alert(apiErrorMessage(err, t('pages.managerStudents.removeFailed')));
        }
    };

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.managerStudents.title')}
                subtitle={t('pages.managerStudents.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getUserListExportColumns(t)}
                            data={filtered}
                            filename="students"
                            disabled={filtered.length === 0}
                        />
                        <ImportStudentsButton onComplete={loadStudents} />
                        <button
                            type="button"
                            onClick={() => navigate('/manager/students/new')}
                            className="btn-page-action-primary"
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('pages.managerStudents.addStudent')}
                        </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={tabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.managerStudents.searchPlaceholder')}
                shown={pagination.pageItems.length}
                total={total}
                entityLabel={t('pages.managerStudents.entityLabel')}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? null : filtered.length === 0 ? (
                <ListEmptyState
                    message={q ? t('pages.managerStudents.noMatch') : t('pages.managerStudents.empty')}
                    hint={q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <>
                    <ListTableCard>
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="table-header">
                                    <tr>
                                        <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                        <SortableTableHeader label={t('table.studentName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                        <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                        <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                        <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                        <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                        <th className="table-header-cell text-left font-semibold">
                                            {t('nav.playingPlatform', { defaultValue: 'Playing' })}
                                        </th>
                                        <th className="table-header-cell">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {pagination.pageItems.map(u => (
                                        <tr key={String(u.id ?? u.userId ?? u.user_id)} className="table-row">
                                            <td className="table-cell whitespace-nowrap">
                                                <ListRecordIdBadge id={resolveRowId(u)} />
                                            </td>
                                            <td className="table-cell">
                                                {formatFullName(u.firstName, u.middleName, u.lastName) ? (
                                                    <UserLink
                                                        id={u.id ?? u.userId}
                                                        name={formatFullName(u.firstName, u.middleName, u.lastName)}
                                                        playing
                                                    />
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="table-cell">{u.username || <span className="text-gray-400">-</span>}</td>
                                            <td className="table-cell">{u.email || <span className="text-gray-400">-</span>}</td>
                                            <td className="table-cell">{u.phoneNumber || <span className="text-gray-400">-</span>}</td>
                                            <td className="table-cell">
                                                <UserStatusBadge active={u.active} />
                                            </td>
                                            <td className="table-cell">
                                                <PlayingPeekCell userId={u.id ?? u.userId} />
                                            </td>
                                            <td className="table-cell whitespace-nowrap">
                                                <UserRowActions
                                                    variant="manager"
                                                    user={u}
                                                    entityLabel={t('pages.managerStudents.entitySingular')}
                                                    editPath={`/manager/students/${u.id ?? u.userId}`}
                                                    onStatusChange={loadStudents}
                                                    onRemoveFromList={removeFromMyList}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ListTableCard>

                    <PaginationFooter
                        page={pagination.page}
                        size={pagination.size}
                        total={pagination.total}
                        onPageChange={pagination.setPage}
                        onSizeChange={pagination.setSize}
                    />
                </>
            )}
        </ListPageLayout>
    );
};

export default ManagerStudentList;
